import React, { useState, useCallback, useRef, useEffect } from 'react';
import { AutoComplete, Tooltip, message } from 'antd';
import { Icon } from 'antd';
import {
  MetaTagsInputWrapper,
  KeyInputContainer,
  ValueInputContainer,
  AddButton,
} from './styles';
import {
  fetchMetaTagKeySuggestions,
  fetchMetaTagValueSuggestions,
} from '../../utils/services/complexDocumentsAPI';

// Default scopeType - can be overridden via props
const DEFAULT_SCOPE_TYPE = 'flow-group';

const debounce = (func, delay) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => func(...args), delay);
  };
};

const MetaTagsInput = ({
  groupId,
  scopeType = DEFAULT_SCOPE_TYPE,
  onAdd,
  disabled = false,
  t,
  keyPlaceholder = 'Enter key',
  valuePlaceholder = 'Enter value',
  addButtonTooltip = 'Add meta tag',
  existingMetaTags = [], // Array of existing meta tags to validate against
}) => {
  const [keyValue, setKeyValue] = useState('');
  const [valueValue, setValueValue] = useState('');
  const [loadingKeys, setLoadingKeys] = useState(false);
  const [loadingValues, setLoadingValues] = useState(false);
  const [keyOptions, setKeyOptions] = useState([]);
  const [valueOptions, setValueOptions] = useState([]);
  const [keyExists, setKeyExists] = useState(false);

  // Fetch value suggestions when key changes
  useEffect(() => {
    if (keyValue && keyValue.trim().length > 0 && !keyExists) {
      debouncedFetchValues(keyValue);
    } else {
      setValueOptions([]);
    }
  }, [keyValue, keyExists, debouncedFetchValues]);

  const debouncedFetchKeys = useRef(
    debounce(async (query) => {
      // Suggestions are organization-wide, so groupId is not required
      if (!query || query.trim().length === 0) {
        setKeyOptions([]);
        return;
      }

      setLoadingKeys(true);
      try {
        const response = await fetchMetaTagKeySuggestions(scopeType, query.trim());
        // Response format may vary - handle both array and object formats
        let suggestionsData = [];
        if (response && response.suggestions) {
          suggestionsData = response.suggestions;
        }
        
        const suggestions = suggestionsData.map((item) => ({
          value: item.key,
          label:  item.key
        }));
        setKeyOptions(suggestions);
      } catch (error) {
        console.error('Error fetching key suggestions:', error);
        setKeyOptions([]);
      } finally {
        setLoadingKeys(false);
      }
    }, 300)
  ).current;

  const debouncedFetchValues = useRef(
    debounce(async (key) => {
      if (!groupId || !key || key.trim().length === 0) {
        setValueOptions([]);
        return;
      }

      setLoadingValues(true);
      try {
        const response = await fetchMetaTagValueSuggestions(scopeType, key.trim(), '');
        // Response format may vary - handle both array and object formats
        let suggestionsData = [];
        if (response && response.suggestions) {
          suggestionsData = response.suggestions;
        }
        
        const suggestions = suggestionsData.map((item) => ({
          value: item.value,
          label: item.value
        }));
        setValueOptions(suggestions);
      } catch (error) {
        console.error('Error fetching value suggestions:', error);
        setValueOptions([]);
      } finally {
        setLoadingValues(false);
      }
    }, 300)
  ).current;

  const handleKeyChange = useCallback(
    (value) => {
      setKeyValue(value);
      
      // Validate if key already exists
      if (value && value.trim().length > 0) {
        const trimmedKey = value.trim();
        const exists = existingMetaTags.some(
          tag => tag.key && tag.key.toLowerCase() === trimmedKey.toLowerCase()
        );
        setKeyExists(exists);
        
        if (!exists) {
          debouncedFetchKeys(value);
        } else {
          setKeyOptions([]);
        }
      } else {
        setKeyExists(false);
        setKeyOptions([]);
      }
    },
    [debouncedFetchKeys, existingMetaTags]
  );

  const handleKeySearch = useCallback(
    (value) => {
      handleKeyChange(value);
    },
    [handleKeyChange]
  );

  const handleValueChange = useCallback(
    (value) => {
      setValueValue(value);
      // Don't call API here - suggestions are already loaded when key was set
    },
    []
  );

  const handleValueSearch = useCallback(
    (value) => {
      handleValueChange(value);
    },
    [handleValueChange]
  );

  const handleAdd = useCallback(() => {
    const trimmedKey = keyValue.trim();
    const trimmedValue = valueValue.trim();

    if (!trimmedKey || trimmedKey.length === 0) {
      message.error(t ? t('META_TAGS.KEY_REQUIRED', 'Key is required') : 'Key is required');
      return;
    }

    if (!trimmedValue || trimmedValue.length === 0) {
      message.error(t ? t('META_TAGS.VALUE_REQUIRED', 'Value is required') : 'Value is required');
      return;
    }

    // Final validation - check if key exists
    const exists = existingMetaTags.some(
      tag => tag.key && tag.key.toLowerCase() === trimmedKey.toLowerCase()
    );
    
    if (exists) {
      message.error(
        t ? t('META_TAGS.KEY_EXISTS_ERROR', 'This key already exists. Please use a different key.') 
          : 'This key already exists. Please use a different key.'
      );
      return;
    }

    if (onAdd) {
      onAdd(trimmedKey, trimmedValue);
      setKeyValue('');
      setValueValue('');
      setKeyExists(false);
      setKeyOptions([]);
      setValueOptions([]);
    }
  }, [keyValue, valueValue, onAdd, t, existingMetaTags]);

  const handleKeySelect = useCallback((value) => {
    setKeyValue(value);
    
    // Validate if selected key already exists
    if (value && value.trim().length > 0) {
      const trimmedKey = value.trim();
      const exists = existingMetaTags.some(
        tag => tag.key && tag.key.toLowerCase() === trimmedKey.toLowerCase()
      );
      setKeyExists(exists);
      if (exists) {
        message.error(
          t ? t('META_TAGS.KEY_EXISTS_ERROR', 'This key already exists. Please use a different key.') 
            : 'This key already exists. Please use a different key.'
        );
      }
    } else {
      setKeyExists(false);
    }
    
    setKeyOptions([]);
  }, [existingMetaTags, t]);

  const handleValueSelect = useCallback((value) => {
    setValueValue(value);
    setValueOptions([]);
  }, []);

  const canAdd = keyValue?.trim().length > 0 && valueValue?.trim().length > 0 && !disabled && !keyExists;

  return (
    <MetaTagsInputWrapper>
      <KeyInputContainer hasError={keyExists}>
        <AutoComplete
          value={keyValue}
          dataSource={keyOptions.map(opt => {
            if (typeof opt === 'string') return opt;
            return opt.value || opt.label || '';
          }).filter(Boolean)}
          onSearch={handleKeySearch}
          onChange={handleKeyChange}
          onSelect={handleKeySelect}
          onBlur={() => {
            // Validate on blur as well
            if (keyValue && keyValue.trim().length > 0) {
              const trimmedKey = keyValue.trim();
              const exists = existingMetaTags.some(
                tag => tag.key && tag.key.toLowerCase() === trimmedKey.toLowerCase()
              );
              setKeyExists(exists);
              if (exists) {
                message.error(
                  t ? t('META_TAGS.KEY_EXISTS_ERROR', 'This key already exists. Please use a different key.') 
                    : 'This key already exists. Please use a different key.'
                );
              }
            }
          }}
          placeholder={keyPlaceholder}
          disabled={disabled}
          allowClear
          notFoundContent={loadingKeys ? 'Loading...' : null}
          style={{ width: '100%' }}
          filterOption={false}
        />
      </KeyInputContainer>
      <ValueInputContainer>
        <AutoComplete
          value={valueValue}
          dataSource={valueOptions.map(opt => {
            if (typeof opt === 'string') return opt;
            return opt.value || opt.label || '';
          }).filter(Boolean)}
          onSearch={handleValueSearch}
          onChange={handleValueChange}
          onSelect={handleValueSelect}
          placeholder={valuePlaceholder}
          disabled={disabled || !keyValue || keyValue.trim().length === 0}
          allowClear
          notFoundContent={loadingValues ? 'Loading...' : null}
          style={{ width: '100%' }}
          filterOption={(input, option) => {
            // Filter suggestions locally based on user input
            const optionValue = typeof option === 'string' ? option : option.value || '';
            return optionValue.toLowerCase().includes(input.toLowerCase());
          }}
        />
      </ValueInputContainer>
      <Tooltip title={addButtonTooltip}>
        <AddButton
          type="primary"
          icon={"plus"}
          onClick={handleAdd}
          disabled={!canAdd}
        />
      </Tooltip>
    </MetaTagsInputWrapper>
  );
};

export default MetaTagsInput;

